import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useSignUp, useAuth } from '@clerk/clerk-react';
import { useState, useCallback, useEffect, useRef } from 'react';
import { track } from '@/lib/analytics';
import { FiChevronLeft } from 'react-icons/fi';
import { Button, Card, Input } from '@/components';
import { isClerkStrategyNotAllowedError } from '@/lib/clerkProEmail';

function proCallbackUrl(): string {
  if (typeof window === 'undefined') return '/pro-callback';
  return `${window.location.origin}/pro-callback`;
}

type Phase = 'email' | 'link_sent' | 'code_entry';

function SignUpProPage() {
  const { isLoaded, signUp, setActive } = useSignUp();
  const { isSignedIn, isLoaded: authLoaded } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    track('Pro Sign Up Screen');
  }, []);

  useEffect(() => {
    if (authLoaded && isSignedIn) {
      navigate({ to: '/pro' });
    }
  }, [authLoaded, isSignedIn, navigate]);

  const [email, setEmail] = useState('');
  const [phase, setPhase] = useState<Phase>('email');
  const [code, setCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const cancelLinkFlowRef = useRef<(() => void) | null>(null);
  const codeInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (phase === 'code_entry') {
      const t = setTimeout(() => codeInputRef.current?.focus(), 100);
      return () => clearTimeout(t);
    }
  }, [phase]);

  useEffect(() => {
    return () => {
      cancelLinkFlowRef.current?.();
    };
  }, []);

  const startSignUp = useCallback(async () => {
    if (!isLoaded || !signUp) return;

    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !trimmed.includes('@')) {
      setError('Please enter a valid email address.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      await signUp.create({ emailAddress: trimmed });

      try {
        const { startEmailLinkFlow, cancelEmailLinkFlow } =
          signUp.createEmailLinkFlow();
        cancelLinkFlowRef.current = cancelEmailLinkFlow;

        setPhase('link_sent');
        setIsSubmitting(false);

        const completed = await startEmailLinkFlow({
          redirectUrl: proCallbackUrl(),
        });

        console.log('[ProSignUp] startEmailLinkFlow resolved:', {
          status: completed.status,
          createdSessionId: completed.createdSessionId,
          missingFields: completed.missingFields,
          unverifiedFields: completed.unverifiedFields,
          requiredFields: completed.requiredFields,
        });

        if (completed.status === 'complete' && completed.createdSessionId) {
          await setActive({ session: completed.createdSessionId });
        } else {
          const verStatus =
            completed.verifications?.emailAddress?.status;
          if (verStatus === 'expired') {
            setError('The email link has expired. Please try again.');
            setPhase('email');
          } else {
            console.error(
              '[ProSignUp] Sign-up not complete. Missing fields:',
              completed.missingFields,
              'Unverified:',
              completed.unverifiedFields,
            );
            setError(
              `Sign-up incomplete (${verStatus ?? completed.status ?? 'unknown'}). Please try again.`
            );
            setPhase('email');
          }
        }
      } catch (linkErr: unknown) {
        if (!isClerkStrategyNotAllowedError(linkErr)) {
          throw linkErr;
        }
        await signUp.prepareEmailAddressVerification({
          strategy: 'email_code',
        });
        setPhase('code_entry');
      }
    } catch (err: unknown) {
      console.error('[ProSignUp] Error:', err);
      const clerkError = err as {
        errors?: { message?: string; longMessage?: string }[];
        message?: string;
      };
      const e0 = clerkError?.errors?.[0];
      const message =
        e0?.longMessage ??
        e0?.message ??
        clerkError?.message ??
        "Couldn't start sign-up. Please try again.";
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  }, [email, isLoaded, signUp, setActive]);

  const switchToCode = useCallback(async () => {
    if (!isLoaded || !signUp) return;
    cancelLinkFlowRef.current?.();
    cancelLinkFlowRef.current = null;
    setIsSubmitting(true);
    setError('');
    try {
      await signUp.prepareEmailAddressVerification({
        strategy: 'email_code',
      });
      setPhase('code_entry');
    } catch (err: unknown) {
      console.error('[ProSignUp] switchToCode error:', err);
      setError('Could not send code. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [isLoaded, signUp]);

  const verifyCode = useCallback(async () => {
    if (!isLoaded || !signUp) return;
    if (!code.trim()) {
      setError('Please enter the code from your email.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const res = await signUp.attemptEmailAddressVerification({
        code: code.trim(),
      });

      if (res.status === 'complete' && res.createdSessionId) {
        await setActive({ session: res.createdSessionId });
      } else {
        setError(`Sign-up incomplete. Status: ${res.status ?? 'unknown'}`);
      }
    } catch (err: unknown) {
      console.error('[ProSignUp] Verify:', err);
      const clerkError = err as {
        errors?: { message?: string }[];
        message?: string;
      };
      const message =
        clerkError?.errors?.[0]?.message ??
        clerkError?.message ??
        "That code didn't work. Please try again.";
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  }, [code, isLoaded, signUp, setActive]);

  useEffect(() => {
    if (
      phase === 'code_entry' &&
      code.length === 6 &&
      !isSubmitting &&
      isLoaded
    ) {
      verifyCode();
    }
  }, [code, phase, isSubmitting, isLoaded, verifyCode]);

  const resetFlow = () => {
    cancelLinkFlowRef.current?.();
    cancelLinkFlowRef.current = null;
    setPhase('email');
    setCode('');
    setError('');
  };

  return (
    <div className='min-h-screen bg-sky-light'>
      <div className='max-w-md mx-auto px-6 py-8'>
        <Link
          to='/'
          search={{ hero: undefined }}
          className='inline-flex items-center text-gray-900 hover:text-primary mb-6 -ml-2'
        >
          <FiChevronLeft size={24} />
        </Link>

        <div className='text-center mb-8'>
          <h1
            className='text-3xl font-semibold text-gray-900 mb-3'
            style={{
              fontFamily:
                "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            }}
          >
            {phase === 'email'
              ? 'Create your account'
              : phase === 'link_sent'
                ? 'Check your email'
                : 'Enter your code'}
          </h1>
          <p
            className='text-lg text-gray-500'
            style={{
              fontFamily:
                "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            }}
          >
            {phase === 'email'
              ? 'For lawyers, mediators, and coaches. We will email you a secure link.'
              : phase === 'link_sent'
                ? 'We sent a verification link to your email. Click it to continue.'
                : 'We sent a 6-digit code to your email.'}
          </p>
        </div>

        <Card>
          {phase === 'email' ? (
            <>
              <Input
                label='Email'
                type='email'
                autoComplete='email'
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isSubmitting}
                autoFocus
              />

              {error && (
                <p className='mt-3 text-sm text-red-500 text-center'>{error}</p>
              )}

              <div className='mt-4'>
                <Button
                  fullWidth
                  disabled={!isLoaded || isSubmitting}
                  loading={isSubmitting}
                  onClick={startSignUp}
                >
                  Continue
                </Button>
              </div>
            </>
          ) : phase === 'link_sent' ? (
            <div className='text-center text-gray-600 text-sm'>
              <p className='mb-1'>
                Open the email at <span className='font-semibold'>{email}</span>{' '}
                and click the link.
              </p>
              <p className='mb-4 text-xs text-gray-400'>
                This page will update automatically once you do.
              </p>

              {error && (
                <p className='mb-4 text-sm text-red-500'>{error}</p>
              )}

              <Button
                variant='secondary'
                fullWidth
                disabled={isSubmitting}
                onClick={switchToCode}
              >
                Send me a code instead
              </Button>

              <div className='mt-3'>
                <Button
                  variant='secondary'
                  fullWidth
                  disabled={isSubmitting}
                  onClick={resetFlow}
                >
                  Use a different email
                </Button>
              </div>
            </div>
          ) : (
            <>
              <Input
                ref={codeInputRef}
                label='Code'
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder='123456'
                type='text'
                inputMode='numeric'
                maxLength={6}
                disabled={isSubmitting}
                autoFocus
              />

              {error && (
                <p className='mt-3 text-sm text-red-500 text-center'>{error}</p>
              )}

              <div className='mt-4'>
                <Button
                  fullWidth
                  disabled={!isLoaded || isSubmitting}
                  loading={isSubmitting}
                  onClick={verifyCode}
                >
                  Continue
                </Button>
              </div>

              <div className='mt-3'>
                <Button
                  variant='secondary'
                  fullWidth
                  disabled={isSubmitting}
                  onClick={resetFlow}
                >
                  Use a different email
                </Button>
              </div>
            </>
          )}
        </Card>

        <div id='clerk-captcha' />

        <div className='flex flex-col items-center gap-3 mt-6 text-center'>
          <div className='flex items-center justify-center gap-2 flex-wrap'>
            <span className='text-gray-500'>Already have an account?</span>
            <Link
              to='/auth/pro/sign-in'
              className='text-gray-900 font-bold hover:underline'
            >
              Sign in
            </Link>
          </div>
          <Link
            to='/auth/sign-up'
            className='text-sm text-primary font-semibold hover:underline'
          >
            Co-parent? Sign up with phone
          </Link>
        </div>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/auth/pro/sign-up')({
  component: SignUpProPage,
});
